<template>
  <TransitionRoot
    as="div"
    :show="open"
  >
    <Dialog
      as="div"
      class="fixed inset-0 overflow-hidden z-20"
      @close="close"
    >
      <div class="absolute inset-0 overflow-hidden">
        <DialogOverlay class="absolute inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />

        <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-300"
            enter-from="translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-300"
            leave-from="translate-x-0"
            leave-to="translate-x-full"
          >
            <div class="w-screen max-w-md">
              <div class="h-full flex flex-col bg-white/95 backdrop-blur-xl shadow-2xl border-l border-gray-200/50 overflow-hidden">
                <!-- Modern Header -->
                <div class="flex-shrink-0 px-6 py-6 bg-gradient-to-r from-primary/5 to-indigo-50/50 border-b border-gray-200/60">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center gap-3">
                      <div class="p-2 bg-primary/10 rounded-xl">
                        <BellIcon class="h-6 w-6 text-primary" />
                      </div>
                      <div>
                        <DialogTitle class="text-xl font-bold text-gray-900">
                          {{ t('messages.notification', 2) }}
                        </DialogTitle>
                        <p
                          class="text-sm text-gray-600 mt-0.5"
                          v-text="t('Stay updated with your latest activities')"
                        />
                      </div>
                    </div>

                    <button
                      type="button"
                      class="p-2 bg-white/80 hover:bg-white rounded-xl text-gray-400 hover:text-gray-600 transition-all duration-200 shadow-sm hover:shadow-md border border-gray-200/60"
                      @click="close"
                    >
                      <XMarkIcon class="h-5 w-5" />
                    </button>
                  </div>
                </div>

                <!-- Notifications List -->
                <div class="flex-1 overflow-hidden">
                  <div class="h-full overflow-y-auto px-4 py-4 space-y-3">
                    <div
                      v-for="notification in paginatedNotifications"
                      :key="notification.id"
                      class="group relative"
                    >
                      <div
                        class="relative bg-white rounded-xl shadow-sm hover:shadow-md border transition-all duration-200 overflow-hidden"
                        :class="[
                          notification.read_at 
                            ? 'border-gray-200/60 hover:border-gray-300/60' 
                            : 'border-primary/20 bg-gradient-to-r from-primary/5 to-transparent ring-1 ring-primary/10'
                        ]"
                      >
                        <!-- Unread indicator -->
                        <div
                          v-if="!notification.read_at"
                          class="absolute top-4 left-4 w-2.5 h-2.5 bg-primary rounded-full animate-pulse"
                        />
                        
                        <div
                          class="p-4"
                          :class="[notification.read_at ? 'pl-4' : 'pl-10']"
                        >
                          <div class="flex items-start justify-between gap-4">
                            <div class="flex-1 min-w-0">
                              <!-- Notification Icon -->
                              <div class="flex items-start gap-3">
                                <div 
                                  class="flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center"
                                  :class="[
                                    notification.read_at 
                                      ? 'bg-gray-100 text-gray-500'
                                      : 'bg-primary/10 text-primary'
                                  ]"
                                >
                                  <CheckCircleIcon 
                                    v-if="notification.read_at" 
                                    class="w-5 h-5" 
                                  />
                                  <BellAlertIcon
                                    v-else
                                    class="w-5 h-5"
                                  />
                                </div>
                                
                                <div class="flex-1 min-w-0">
                                  <p 
                                    class="text-sm leading-relaxed font-medium"
                                    :class="[
                                      notification.read_at 
                                        ? 'text-gray-600'
                                        : 'text-gray-900'
                                    ]"
                                    v-text="notification.data?.message"
                                  />
                                  
                                  <!-- Timestamp -->
                                  <div class="mt-2 flex items-center gap-2 text-xs text-gray-500">
                                    <ClockIcon class="w-3 h-3" />
                                    <time v-text="notification.created_at_diff_humans" />
                                  </div>
                                </div>
                              </div>
                            </div>

                            <!-- Action Button -->
                            <div
                              v-if="notification.data?.url"
                              class="flex-shrink-0"
                            >
                              <a
                                :href="notification.data?.url"
                                class="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-primary hover:text-indigo-700 bg-primary/5 hover:bg-primary/10 rounded-lg border border-primary/20 hover:border-primary/30 transition-all duration-200"
                              >
                                <span v-text=" t('messages.details')" />
                                <ArrowRightIcon class="w-3 h-3" />
                              </a>
                            </div>
                          </div>
                        </div>

                        <!-- Hover effect overlay -->
                        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
                      </div>
                    </div>

                    <!-- Observer for infinite scroll -->
                    <Observer @intersect="fetchNotifications" />
                  </div>
                </div>

                <!-- Loading and Empty States -->
                <div class="flex-shrink-0 px-6 py-4 border-t border-gray-200/60 bg-gray-50/50">
                  <div class="flex justify-center">
                    <div
                      v-if="loadingNotifications"
                      class="flex items-center gap-2 text-sm text-gray-600"
                    >
                      <ArrowPathIcon class="w-4 h-4 animate-spin" />
                      <span v-text="t('Loading...')" />
                    </div>
                    
                    <div
                      v-else-if="!hasMoreNotifications && !hasNoNotifications"
                      class="flex items-center gap-2 text-sm text-gray-500"
                    >
                      <CheckCircleIcon class="w-4 h-4" />
                      <span v-text="t('messages.no_more_notifications')" />
                    </div>

                    <div
                      v-else-if="hasNoNotifications"
                      class="text-center"
                    >
                      <div class="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <BellIcon class="w-6 h-6 text-gray-400" />
                      </div>
                      <p
                        class="text-sm font-medium text-gray-900 mb-1"
                        v-text="t('messages.no_notifications')"
                      />
                      <p
                        class="text-xs text-gray-500"
                        v-text="t('You have no notifications at the moment.')"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script lang="ts" setup>
import {
  XMarkIcon,
  CheckCircleIcon,
  ArrowRightIcon,
} from '@heroicons/vue/24/solid';
import {
  ArrowPathIcon,
  BellIcon,
  ClockIcon,
  BellAlertIcon,
} from '@heroicons/vue/24/outline';
import {
  Dialog,
  DialogOverlay,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { useI18n } from 'vue-i18n';
import Observer from '@/Components/Observer.vue';
import useNotifications from '@/composables/useNotifications';

defineProps({
  open: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();

const emit = defineEmits(['close']);

const {
  paginatedNotifications,
  fetchNotifications,
  loadingNotifications,
  hasMoreNotifications,
  hasNoNotifications,
} = useNotifications();

function close() {
  hasMoreNotifications.value = true;
  emit('close');
}

</script>
